import { inject, type InjectionKey } from 'vue'

// Address the portal is served on. On a tenant address (acme.aubaine.com.br or the tenant's own
// domain) the tenant comes from the host and the routes sit at the root (/usuarios). On the
// central host the routes carry the tenant (/cliente-acme/usuarios), so a platform admin can
// open any company.
export interface HostTenant {
  tenantId: string
  host: string
  isPrimary: boolean
  primaryUrl: string
}

export interface PortalHost {
  // Tenant of this address, or null on the central host.
  tenantId: string | null
  // In-app path of a tenant's screen, e.g. path('cliente-acme', 'usuarios').
  path: (tenantId: string, screen?: string) => string
}

export function createPortalHost(tenantId: string | null): PortalHost {
  return {
    tenantId,
    path: (id, screen = '') => (id === tenantId ? `/${screen}` : `/${id}/${screen}`),
  }
}

export const PORTAL_HOST: InjectionKey<PortalHost> = Symbol('portal-host')

export function usePortalHost(): PortalHost {
  return inject(PORTAL_HOST, () => createPortalHost(null), true)
}

// Anonymous lookup (GET /api/public/domains/resolve). null = not a tenant address: central host,
// feature off or unknown host. A failure keeps the central behaviour.
export async function resolveHostTenant(apiBaseUrl: string, host = window.location.hostname): Promise<HostTenant | null> {
  try {
    const root = apiBaseUrl.replace(/\/+$/, '')
    const response = await fetch(`${root}/public/domains/resolve?host=${encodeURIComponent(host)}`, {
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return null
    const data = (await response.json()) as Partial<HostTenant>
    return typeof data.tenantId === 'string' && typeof data.primaryUrl === 'string'
      ? { tenantId: data.tenantId, host: data.host ?? host, isPrimary: data.isPrimary ?? false, primaryUrl: data.primaryUrl }
      : null
  } catch {
    return null
  }
}
